import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Package,
  FileText,
  Users,
  X,
  ArrowRight,
} from 'lucide-react';
import { analyticsService } from '../services/analyticsService';
import { useLanguage } from '../hooks/useTranslation';

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

type ResultItem = {
  id: string;
  label: string;
  sub: string;
  path: string;
  category: 'colis' | 'factures' | 'livreurs';
};

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isEn = language === 'en';

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(timer);
  }, [q]);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setQ('');
      setDebouncedQ('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', debouncedQ],
    queryFn: () => analyticsService.searchGlobal(debouncedQ),
    enabled: debouncedQ.length >= 2,
    staleTime: 10_000,
  });

  // Memoize flattened results — prevents new array ref on every render (which would
  // cause the keyboard useEffect to remove/re-add listeners on every keystroke)
  const results: ResultItem[] = useMemo(() => {
    if (!data) return [];
    const items: ResultItem[] = [];
    data.colis.forEach((c: any) => {
      items.push({
        id: c.id,
        label: c.tracking_number,
        sub: c.destinataire_nom + (c.destinataire_ville ? ` — ${c.destinataire_ville}` : ''),
        path: `/colis/${c.id}`,
        category: 'colis',
      });
    });
    data.factures.forEach((f: any) => {
      items.push({
        id: f.id,
        label: f.numero_facture,
        sub: f.client_nom || '',
        path: `/factures/${f.id}`,
        category: 'factures',
      });
    });
    data.livreurs.forEach((l: any) => {
      items.push({
        id: l.id,
        label: l.nom,
        sub: l.telephone || '',
        path: `/livreurs/${l.id}`,
        category: 'livreurs',
      });
    });
    return items;
  }, [data]);

  // Keep refs to latest results + activeIndex so the keyboard handler can read
  // current values without being in the dependency array (avoids listener churn)
  const resultsRef = useRef(results);
  const activeIndexRef = useRef(activeIndex);
  resultsRef.current = results;
  activeIndexRef.current = activeIndex;

  const handleSelect = useCallback(
    (item: ResultItem) => {
      navigate(item.path);
      onClose();
    },
    [navigate, onClose],
  );
  const handleSelectRef = useRef(handleSelect);
  handleSelectRef.current = handleSelect;

  // Keyboard navigation — depends only on `open` + `onClose` (both stable).
  // Reads live results/activeIndex via refs → listener is added once per open cycle.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, resultsRef.current.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter' && resultsRef.current.length > 0) {
        handleSelectRef.current(resultsRef.current[activeIndexRef.current]);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Reset active index when query changes
  useEffect(() => {
    setActiveIndex(0);
  }, [debouncedQ]);

  if (!open) return null;

  const CategoryIcon = ({ cat }: { cat: ResultItem['category'] }) => {
    if (cat === 'colis') return <Package className="w-4 h-4 text-purple-400" />;
    if (cat === 'factures') return <FileText className="w-4 h-4 text-blue-400" />;
    return <Users className="w-4 h-4 text-green-400" />;
  };

  const categoryLabel = (cat: ResultItem['category']) => {
    if (cat === 'colis') return isEn ? 'Parcel' : 'Colis';
    if (cat === 'factures') return isEn ? 'Invoice' : 'Facture';
    return isEn ? 'Driver' : 'Livreur';
  };

  const showEmpty = debouncedQ.length >= 2 && !isFetching && results.length === 0;
  const showHint = debouncedQ.length < 2 && !isFetching;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-xl bg-slate-900/95 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-white/10">
          <Search className="w-5 h-5 text-white/60 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={isEn ? 'Search parcels, invoices, drivers...' : 'Rechercher colis, factures, livreurs...'}
            className="flex-1 bg-transparent text-white placeholder-white/50 outline-none text-base"
          />
          {q && (
            <button onClick={() => setQ('')} className="text-white/40 hover:text-white/70 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white/40 text-xs font-mono">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {isFetching && (
            <div className="flex items-center justify-center py-8">
              <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
            </div>
          )}

          {showHint && (
            <div className="px-4 py-8 text-center text-white/40 text-sm">
              {isEn ? 'Type at least 2 characters to search' : 'Saisissez au moins 2 caractères'}
            </div>
          )}

          {showEmpty && (
            <div className="px-4 py-8 text-center text-white/40 text-sm">
              {isEn ? `No results for "${debouncedQ}"` : `Aucun résultat pour « ${debouncedQ} »`}
            </div>
          )}

          {!isFetching && results.length > 0 && (
            <ul className="py-2">
              {results.map((item, idx) => (
                <li key={`${item.category}-${item.id}`}>
                  <button
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      idx === activeIndex ? 'bg-white/10' : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <CategoryIcon cat={item.category} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{item.label}</p>
                      {item.sub && (
                        <p className="text-white/50 text-xs truncate">{item.sub}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-white/30 text-xs">{categoryLabel(item.category)}</span>
                      {idx === activeIndex && <ArrowRight className="w-3.5 h-3.5 text-white/40" />}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 text-white/30 text-xs">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono">↓</kbd>
              {isEn ? 'Navigate' : 'Naviguer'}
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded font-mono">↵</kbd>
              {isEn ? 'Select' : 'Sélectionner'}
            </span>
          </span>
          {data && data.total > 0 && (
            <span>{data.total} {isEn ? 'result(s)' : 'résultat(s)'}</span>
          )}
        </div>
      </div>
    </div>
  );
};
